import React, { useCallback, useEffect, useState } from 'react';
import { branchApi, resolveBranchApiError } from '../services/branchApi';
import FdModal from '../components/FdModal';
import useScopePermissions from '../hooks/useScopePermissions';

function resolveCandidateIdByName(candidates, name) {
  const normalizedName = String(name || '').trim().toLowerCase();
  if (!normalizedName || !Array.isArray(candidates)) {
    return '';
  }

  const matched = candidates.find((candidate) => (
    String(candidate?.full_name || '').trim().toLowerCase() === normalizedName
  ));

  return matched?.id || '';
}

export default function BranchManagementPage() {
  const { hasScope } = useScopePermissions();
  const canCreate = hasScope('branch-management.create');
  const canUpdate = hasScope('branch-management.update');
  const canDelete = hasScope('branch-management.delete');

  const [branches, setBranches] = useState([]);
  const [areaHeadCandidates, setAreaHeadCandidates] = useState([]);
  const [assistantAreaHeadCandidates, setAssistantAreaHeadCandidates] = useState([]);
  const [divisionHeadCandidates, setDivisionHeadCandidates] = useState([]);
  const [ceoCandidates, setCeoCandidates] = useState([]);
  const [seniorAreaHeadCandidates, setSeniorAreaHeadCandidates] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [selectedBranch, setSelectedBranch] = useState(null);
  const [filters, setFilters] = useState({ search: '', status: 'all' });
  const [formData, setFormData] = useState({
    name: '',
    code: '',
    address: '',
    area_head_agent_id: '',
    assistant_area_head_agent_id: '',
    division_head_agent_id: '',
    ceo_agent_id: '',
    senior_area_head_agent_id: '',
    area_head: '',
    assistant_area_head: '',
    division_head: '',
    ceo: '',
    senior_area_head: '',
    status: 'Active',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const loadBranches = useCallback(async (options = {}) => {
    const { clearFeedback = true } = options;

    setIsLoading(true);
    setError('');
    if (clearFeedback) {
      setMessage('');
    }
    try {
      const response = await branchApi.listBranches({ bustCache: true });
      const list = response?.data?.branches || response?.branches || [];
      const areaHeadList = response?.data?.area_head_candidates || response?.area_head_candidates || [];
      const assistantAreaHeadList = response?.data?.assistant_area_head_candidates || response?.assistant_area_head_candidates || [];
      const divisionHeadList = response?.data?.division_head_candidates || response?.division_head_candidates || [];
      const ceoList = response?.data?.ceo_candidates || response?.ceo_candidates || [];
      const seniorAreaHeadList = response?.data?.senior_area_head_candidates || response?.senior_area_head_candidates || [];
      setBranches(Array.isArray(list) ? list : []);
      setAreaHeadCandidates(Array.isArray(areaHeadList) ? areaHeadList : []);
      setAssistantAreaHeadCandidates(Array.isArray(assistantAreaHeadList) ? assistantAreaHeadList : []);
      setDivisionHeadCandidates(Array.isArray(divisionHeadList) ? divisionHeadList : []);
      setCeoCandidates(Array.isArray(ceoList) ? ceoList : []);
      setSeniorAreaHeadCandidates(Array.isArray(seniorAreaHeadList) ? seniorAreaHeadList : []);
      if (clearFeedback && list.length === 0) setMessage('No branches found.');
    } catch (err) {
      setBranches([]);
      setAreaHeadCandidates([]);
      setAssistantAreaHeadCandidates([]);
      setDivisionHeadCandidates([]);
      setCeoCandidates([]);
      setSeniorAreaHeadCandidates([]);
      setError(resolveBranchApiError(err));
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => { loadBranches(); }, [loadBranches]);

  const filteredBranches = branches.filter((b) => {
    const matchSearch = b.name?.toLowerCase().includes(filters.search.toLowerCase()) ||
                        b.code?.toLowerCase().includes(filters.search.toLowerCase());
    const matchStatus = filters.status === 'all' || b.status === filters.status;
    return matchSearch && matchStatus;
  });

  function handleFilterChange(e) {
    const { name, value } = e.target;
    setFilters((prev) => ({ ...prev, [name]: value }));
  }

  function handleFormChange(e) {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  }

  function openAddForm() {
    if (!canCreate) {
      setError('You do not have permission to add branches.');
      return;
    }

    setIsEditMode(false);
    setSelectedBranch(null);
    setFormData({
      name: '',
      code: '',
      address: '',
      area_head_agent_id: '',
      assistant_area_head_agent_id: '',
      division_head_agent_id: '',
      ceo_agent_id: '',
      senior_area_head_agent_id: '',
      area_head: '',
      assistant_area_head: '',
      division_head: '',
      ceo: '',
      senior_area_head: '',
      status: 'Active',
    });
    setError('');
    setIsFormOpen(true);
  }

  function openEditForm(branch) {
    if (!canUpdate) {
      setError('You do not have permission to edit branches.');
      return;
    }

    const resolvedAreaHeadAgentId = branch.area_head_agent_id
      || resolveCandidateIdByName(areaHeadCandidates, branch.area_head || branch.manager || '');
    const resolvedAssistantAreaHeadAgentId = branch.assistant_area_head_agent_id
      || resolveCandidateIdByName(assistantAreaHeadCandidates, branch.assistant_area_head || '');
    const resolvedDivisionHeadAgentId = branch.division_head_agent_id
      || resolveCandidateIdByName(divisionHeadCandidates, branch.division_head || '');
    const resolvedCeoAgentId = branch.ceo_agent_id
      || resolveCandidateIdByName(ceoCandidates, branch.ceo || '');
    const resolvedSeniorAreaHeadAgentId = branch.senior_area_head_agent_id
      || resolveCandidateIdByName(seniorAreaHeadCandidates, branch.senior_area_head || '');

    setIsEditMode(true);
    setSelectedBranch(branch);
    setFormData({
      name: branch.name || '',
      code: branch.code || '',
      address: branch.address || '',
      area_head_agent_id: resolvedAreaHeadAgentId || '',
      assistant_area_head_agent_id: resolvedAssistantAreaHeadAgentId || '',
      division_head_agent_id: resolvedDivisionHeadAgentId || '',
      ceo_agent_id: resolvedCeoAgentId || '',
      senior_area_head_agent_id: resolvedSeniorAreaHeadAgentId || '',
      area_head: branch.area_head || branch.manager || '',
      assistant_area_head: branch.assistant_area_head || '',
      division_head: branch.division_head || '',
      ceo: branch.ceo || '',
      senior_area_head: branch.senior_area_head || '',
      status: branch.status || 'Active',
    });
    setError('');
    setIsFormOpen(true);
  }

  function closeForm() {
    setIsFormOpen(false);
    setSelectedBranch(null);
    setFormData({
      name: '',
      code: '',
      address: '',
      area_head_agent_id: '',
      assistant_area_head_agent_id: '',
      division_head_agent_id: '',
      ceo_agent_id: '',
      senior_area_head_agent_id: '',
      area_head: '',
      assistant_area_head: '',
      division_head: '',
      ceo: '',
      senior_area_head: '',
      status: 'Active',
    });
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setError('');
    setMessage('');

    if (isEditMode && !canUpdate) {
      setError('You do not have permission to edit branches.');
      return;
    }

    if (!isEditMode && !canCreate) {
      setError('You do not have permission to add branches.');
      return;
    }

    if (!formData.name.trim() || !formData.code.trim()) {
      setError('Branch name and code are required.');
      return;
    }
    setIsSubmitting(true);
    try {
      const areaHeadId = formData.area_head_agent_id ? parseInt(formData.area_head_agent_id, 10) : null;
      const assistantAreaHeadId = formData.assistant_area_head_agent_id ? parseInt(formData.assistant_area_head_agent_id, 10) : null;
      const divisionHeadId = formData.division_head_agent_id ? parseInt(formData.division_head_agent_id, 10) : null;
      const ceoId = formData.ceo_agent_id ? parseInt(formData.ceo_agent_id, 10) : null;
      const seniorAreaHeadId = formData.senior_area_head_agent_id ? parseInt(formData.senior_area_head_agent_id, 10) : null;
      const selectedAreaHead = areaHeadCandidates.find((item) => Number(item.id) === Number(areaHeadId)) || null;
      const selectedAssistantAreaHead = assistantAreaHeadCandidates.find((item) => (
        Number(item.id) === Number(assistantAreaHeadId)
      )) || null;
      const selectedDivisionHead = divisionHeadCandidates.find((item) => Number(item.id) === Number(divisionHeadId)) || null;
      const selectedCeo = ceoCandidates.find((item) => Number(item.id) === Number(ceoId)) || null;
      const selectedSeniorAreaHead = seniorAreaHeadCandidates.find((item) => (
        Number(item.id) === Number(seniorAreaHeadId)
      )) || null;
      const payload = {
        ...formData,
        area_head_agent_id: Number.isFinite(areaHeadId) ? areaHeadId : null,
        assistant_area_head_agent_id: Number.isFinite(assistantAreaHeadId) ? assistantAreaHeadId : null,
        division_head_agent_id: Number.isFinite(divisionHeadId) ? divisionHeadId : null,
        ceo_agent_id: Number.isFinite(ceoId) ? ceoId : null,
        senior_area_head_agent_id: Number.isFinite(seniorAreaHeadId) ? seniorAreaHeadId : null,
        area_head: selectedAreaHead?.full_name || null,
        assistant_area_head: selectedAssistantAreaHead?.full_name || null,
        division_head: selectedDivisionHead?.full_name || null,
        ceo: selectedCeo?.full_name || null,
        senior_area_head: selectedSeniorAreaHead?.full_name || null,
      };

      if (isEditMode) {
        await branchApi.updateBranch(selectedBranch.id, payload);
        setMessage('Branch updated successfully.');
      } else {
        await branchApi.createBranch(payload);
        setMessage('Branch created successfully.');
      }
      closeForm();
      await loadBranches({ clearFeedback: false });
    } catch (err) {
      setError(resolveBranchApiError(err));
    } finally {
      setIsSubmitting(false);
    }
  }

  async function handleDelete(branch) {
    if (!canDelete) {
      setError('You do not have permission to delete branches.');
      return;
    }

    if (!window.confirm(`Are you sure you want to delete "${branch.name}"?`)) return;
    setError('');
    setMessage('');
    setIsSubmitting(true);
    try {
      await branchApi.deleteBranch(branch.id);
      setMessage('Branch deleted successfully.');
      await loadBranches({ clearFeedback: false });
    } catch (err) {
      setError(resolveBranchApiError(err));
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <div className="dashboard-container">
      <section className="dashboard-header">
        <h1>Branch Management</h1>
        <p>Manage your branches and their details.</p>
      </section>

      <section className="dashboard-content">
        <div className="dashboard-toolbar">
          <div className="dashboard-filters">
            <input type="text" name="search" value={filters.search} onChange={handleFilterChange} placeholder="Search branch name or code..." />
            <select name="status" value={filters.status} onChange={handleFilterChange}>
              <option value="all">All Status</option>
              <option value="Active">Active</option>
              <option value="Inactive">Inactive</option>
            </select>
          </div>
          {canCreate ? (
            <button type="button" className="profile-secondary-btn" onClick={openAddForm}>+ Add Branch</button>
          ) : null}
        </div>

        {error && !isFormOpen && <div className="error-box">{error}</div>}
        {message && <div className="success-box">{message}</div>}

        {isLoading ? (
          <div className="dashboard-loading">Loading branches...</div>
        ) : filteredBranches.length === 0 ? (
          <div className="dashboard-empty">No branches found.</div>
        ) : (
          <div className="dashboard-grid">
            {filteredBranches.map((branch) => (
              <div key={branch.id} className="dashboard-card">
                <div className="card-header">
                  <h3>{branch.name}</h3>
                  <span className={`status-badge status-${branch.status?.toLowerCase()}`}>{branch.status}</span>
                </div>
                <div className="card-body">
                  <p><strong>Code:</strong> {branch.code}</p>
                  <p><strong>Area Head:</strong> {branch.area_head || branch.manager || '-'}</p>
                  <p><strong>Assistant Area Head:</strong> {branch.assistant_area_head || '-'}</p>
                  <p><strong>Senior Area Head:</strong> {branch.senior_area_head || '-'}</p>
                  <p><strong>Division Head:</strong> {branch.division_head || '-'}</p>
                  <p><strong>CEO:</strong> {branch.ceo || '-'}</p>
                  <p><strong>Address:</strong> {branch.address || '-'}</p>
                  <p><strong>Created:</strong> {branch.created_at || '-'}</p>
                </div>
                <div className="card-footer">
                  {canUpdate ? (
                    <button type="button" className="profile-secondary-btn profile-secondary-btn-sm" onClick={() => openEditForm(branch)}>Edit</button>
                  ) : null}
                  {canDelete ? (
                    <button type="button" className="profile-danger-btn profile-danger-btn-sm" onClick={() => handleDelete(branch)} disabled={isSubmitting}>Delete</button>
                  ) : null}
                  {!canUpdate && !canDelete ? <span>-</span> : null}
                </div>
              </div>
            ))}
          </div>
        )}
      </section>

      {/* ── Modal: Create / Edit Branch ── */}
      <FdModal
        isOpen={isFormOpen}
        onClose={closeForm}
        eyebrow="Branch Management"
        title={isEditMode ? 'Edit Branch' : 'Create Branch'}
        subtitle="Maintain branch profile and assignment details."
        footer={
          <>
            <button type="button" className="fd-btn fd-btn-cancel" onClick={closeForm} disabled={isSubmitting}>Cancel</button>
            <button
              type="submit"
              form="branch-form"
              className="fd-btn fd-btn-primary"
              disabled={isSubmitting || (isEditMode ? !canUpdate : !canCreate)}
            >
              {isSubmitting ? 'Saving...' : (isEditMode ? 'Update Branch' : 'Create Branch')}
            </button>
          </>
        }
      >
        <form id="branch-form" onSubmit={handleSubmit}>
          <div className="fd-modal-grid">
            <div className="fd-field">
              <label htmlFor="name">Branch Name *</label>
              <input id="name" type="text" name="name" value={formData.name} onChange={handleFormChange} placeholder="Enter branch name" required />
            </div>
            <div className="fd-field">
              <label htmlFor="code">Branch Code *</label>
              <input id="code" type="text" name="code" value={formData.code} onChange={handleFormChange} placeholder="Enter branch code" required />
            </div>
            <div className="fd-field fd-field-wide">
              <label htmlFor="address">Address</label>
              <input id="address" type="text" name="address" value={formData.address} onChange={handleFormChange} placeholder="Enter address" />
            </div>
            <div className="fd-field">
              <label htmlFor="area_head_agent_id">Area Head</label>
              <select
                id="area_head_agent_id"
                name="area_head_agent_id"
                value={formData.area_head_agent_id}
                onChange={handleFormChange}
              >
                <option value="">Select area head</option>
                {areaHeadCandidates.map((candidate) => (
                  <option key={`area-head-${candidate.id}`} value={candidate.id}>
                    {candidate.full_name}
                  </option>
                ))}
              </select>
            </div>
            <div className="fd-field">
              <label htmlFor="assistant_area_head_agent_id">Assistant Area Head</label>
              <select
                id="assistant_area_head_agent_id"
                name="assistant_area_head_agent_id"
                value={formData.assistant_area_head_agent_id}
                onChange={handleFormChange}
              >
                <option value="">Select assistant area head</option>
                {assistantAreaHeadCandidates.map((candidate) => (
                  <option key={`assistant-area-head-${candidate.id}`} value={candidate.id}>
                    {candidate.full_name}
                  </option>
                ))}
              </select>
            </div>
            <div className="fd-field">
              <label htmlFor="senior_area_head_agent_id">Senior Area Head</label>
              <select
                id="senior_area_head_agent_id"
                name="senior_area_head_agent_id"
                value={formData.senior_area_head_agent_id}
                onChange={handleFormChange}
              >
                <option value="">Select senior area head</option>
                {seniorAreaHeadCandidates.map((candidate) => (
                  <option key={`senior-area-head-${candidate.id}`} value={candidate.id}>
                    {candidate.full_name}
                  </option>
                ))}
              </select>
            </div>
            <div className="fd-field">
              <label htmlFor="division_head_agent_id">Division Head</label>
              <select
                id="division_head_agent_id"
                name="division_head_agent_id"
                value={formData.division_head_agent_id}
                onChange={handleFormChange}
              >
                <option value="">Select division head</option>
                {divisionHeadCandidates.map((candidate) => (
                  <option key={`division-head-${candidate.id}`} value={candidate.id}>
                    {candidate.full_name}
                  </option>
                ))}
              </select>
            </div>
            <div className="fd-field">
              <label htmlFor="ceo_agent_id">CEO</label>
              <select
                id="ceo_agent_id"
                name="ceo_agent_id"
                value={formData.ceo_agent_id}
                onChange={handleFormChange}
              >
                <option value="">Select CEO</option>
                {ceoCandidates.map((candidate) => (
                  <option key={`ceo-${candidate.id}`} value={candidate.id}>
                    {candidate.full_name}
                  </option>
                ))}
              </select>
            </div>
            <div className="fd-field">
              <label htmlFor="status">Status</label>
              <select id="status" name="status" value={formData.status} onChange={handleFormChange}>
                <option value="Active">Active</option>
                <option value="Inactive">Inactive</option>
              </select>
            </div>
          </div>
          {error && <div className="error-box" style={{ marginTop: 12 }}>{error}</div>}
        </form>
      </FdModal>
    </div>
  );
}
